import { useCallback, useMemo } from 'react';
import { useExtensionStore } from '../extensions/store';
import { setActiveTab } from '../store/uiModalStore';
import {
  openTaskManagerBoardTab,
  closeTaskManagerBoardTab,
  updateTaskManagerBoardTabLabel,
} from '../store/tabsStore';
import { readBoard, deleteBoard as deleteBoardRecord } from '../extensions/bundled/task-manager/boardsStore';
import { bumpBoards } from '../extensions/bundled/task-manager/boardsUiStore';
import { useAiConnectionActions } from './useAiConnectionActions';
import { useAiDocumentActions } from './useAiDocumentActions';
import { useAiDbActions } from './useAiDbActions';
import { useAiBoardActions } from './useAiBoardActions';

/**
 * AI tool handlers for the bundled extensions (Task Manager, DB Manager,
 * Documents). Returns a map keyed by tool name; AIChatPanel's executeTool
 * dispatches extension tools through it. Handlers operate directly on each
 * extension's host-side store (bundled extensions run in-process), and switch
 * the app to the right panel via activatePanel + setActiveTab so the user sees
 * the AI acting — matching how the Collections tools drive the Request Builder.
 */
export function useAiExtensionActions() {
  const navigateToPanel = useCallback((panelId) => {
    try {
      useExtensionStore.getState().activatePanel(panelId);
    } catch {
      /* activatePanel unavailable (panel not registered) — still set the tab */
    }
    setActiveTab(panelId);
  }, []);

  const {
    findBoardId,
    getBoards,
    getBoard,
    createBoard,
    mutateBoard,
    createList,
    createTask,
    updateTask,
    writeTaskDescription,
  } = useAiBoardActions({ navigateToPanel });

  const { dbListConnections, dbGetSchema, dbRunSql } = useAiDbActions({ navigateToPanel });

  const {
    getDocuments,
    readDoc,
    createDocFolderTool,
    createDocumentTool,
    writeDocumentTool,
    openDocumentTool,
    renameDocumentTool,
    deleteDocumentTool,
  } = useAiDocumentActions({ navigateToPanel });

  // ── Task Manager: open / rename / delete an existing board ──────────────────

  const openBoardTool = useCallback(async (input) => {
    const boardId = await findBoardId(input);
    if (!boardId) return { error: 'Board not found' };
    const board = await readBoard(boardId);
    if (!board) return { error: 'Board not found' };
    navigateToPanel('task-manager.panel');
    openTaskManagerBoardTab({ boardId, label: board.name });
    return { ok: true, name: board.name };
  }, [findBoardId, navigateToPanel]);

  const renameBoardTool = useCallback(async (input) => {
    if (!input.name) return { error: 'name is required' };
    const boardId = await findBoardId(input);
    if (!boardId) return { error: 'Board not found' };
    await mutateBoard(boardId, (board) => { board.name = input.name; });
    // Keep an open board tab's strip label in sync (content title re-reads on bump).
    updateTaskManagerBoardTabLabel(boardId, input.name);
    return { ok: true, name: input.name };
  }, [findBoardId, mutateBoard]);

  const deleteBoardTool = useCallback(async (input) => {
    const boardId = await findBoardId(input);
    if (!boardId) return { error: 'Board not found' };
    const board = await readBoard(boardId);
    const name = board?.name || input.board_name || '';
    await deleteBoardRecord(boardId);
    bumpBoards();
    closeTaskManagerBoardTab(boardId);
    return { ok: true, name };
  }, [findBoardId]);

  const {
    terminalListConnections,
    ftpListConnections,
    ftpListDir,
    terminalRunCommand,
  } = useAiConnectionActions();

  return useMemo(() => ({
    // Task Manager
    get_boards: getBoards,
    get_board: getBoard,
    create_board: createBoard,
    open_board: openBoardTool,
    rename_board: renameBoardTool,
    delete_board: deleteBoardTool,
    create_list: createList,
    create_task: createTask,
    update_task: updateTask,
    write_task_description: writeTaskDescription,
    // DB Manager
    db_list_connections: dbListConnections,
    db_get_schema: dbGetSchema,
    db_run_sql: dbRunSql,
    // Terminal
    terminal_list_connections: terminalListConnections,
    ftp_list_connections: ftpListConnections,
    ftp_list_dir: ftpListDir,
    terminal_run_command: terminalRunCommand,
    // Documents
    get_documents: getDocuments,
    read_document: readDoc,
    create_doc_folder: createDocFolderTool,
    create_document: createDocumentTool,
    open_document: openDocumentTool,
    rename_document: renameDocumentTool,
    delete_document: deleteDocumentTool,
    write_document: writeDocumentTool,
  }), [
    getBoards, getBoard, createBoard, openBoardTool, renameBoardTool, deleteBoardTool,
    createList, createTask, updateTask, writeTaskDescription,
    dbListConnections, dbGetSchema, dbRunSql,
    terminalListConnections, terminalRunCommand,
    ftpListConnections, ftpListDir,
    getDocuments, readDoc, createDocFolderTool, createDocumentTool,
    openDocumentTool, renameDocumentTool, deleteDocumentTool, writeDocumentTool,
  ]);
}

